import React, { useEffect, useState, useCallback } from "react";
import { wasteApi } from "@/api/waste";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, HeartHandshake, Trash2, Check, Loader2 } from "lucide-react";
import EmptyState from "@/components/shared/EmptyState";

const DESTINATIONS = [
  { value: "reuse",    label: "Reutilització interna (menú dia)" },
  { value: "staff",    label: "Personal" },
  { value: "donation", label: "Donació (banc d'aliments…)" },
  { value: "compost",  label: "Compostatge" },
  { value: "discard",  label: "Descart" },
];

const STATUS_COLORS = {
  planned: "bg-blue-100 text-blue-700",
  done: "bg-emerald-100 text-emerald-700",
  discarded: "bg-gray-200 text-gray-700",
};

export default function WasteReuse({ venueId, settings }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    product_name: "", destination: "reuse", destination_name: "",
    quantity: "", unit: "kg", limit_date: "", notes: "", estimated_value: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const data = await wasteApi.listReuse(venueId);
      setItems(data || []);
    } catch (e) { /* noop */ } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_name.trim()) { toast.error("Indica el producte"); return; }
    if (!form.quantity || parseFloat(form.quantity) <= 0) { toast.error("Quantitat invàlida"); return; }
    setSaving(true);
    try {
      await wasteApi.createReuse({
        venue_id: venueId,
        product_name: form.product_name.trim(),
        destination: form.destination,
        destination_name: form.destination_name || null,
        quantity: parseFloat(form.quantity),
        unit: form.unit,
        limit_date: form.limit_date || null,
        notes: form.notes || null,
        estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : 0,
        status: "planned",
      });
      toast.success("Acció registrada");
      setOpen(false);
      setForm({ product_name: "", destination: "reuse", destination_name: "", quantity: "", unit: "kg", limit_date: "", notes: "", estimated_value: "" });
      load();
    } catch (err) {
      toast.error(err.message || "Error guardant");
    } finally {
      setSaving(false);
    }
  };

  const markDone = async (it) => {
    try {
      await wasteApi.updateReuse(it.reuse_action_id, { status: "done", completed_at: new Date().toISOString() });
      toast.success("Marcat com a completat");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const remove = async (it) => {
    if (!window.confirm("Eliminar aquesta acció?")) return;
    try {
      await wasteApi.deleteReuse(it.reuse_action_id);
      toast.success("Eliminat");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const units = settings?.units || ["kg", "g", "l", "ml", "unitats"];
  const destLabel = (v) => DESTINATIONS.find((d) => d.value === v)?.label || v;

  return (
    <div className="space-y-4" data-testid="waste-reuse">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Registra accions de reutilització, donació o compostatge per evitar el descart.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#7B2D3F] hover:bg-[#5B1D2F]" data-testid="waste-reuse-add">
              <Plus className="w-4 h-4 mr-2" /> Nova acció
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Nova acció de reutilització</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs font-semibold">Producte *</Label>
                <Input data-testid="reuse-product" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs font-semibold">Quantitat *</Label>
                  <Input type="number" min="0" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="reuse-quantity" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Unitat</Label>
                  <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Destinació *</Label>
                <Select value={form.destination} onValueChange={(v) => setForm({ ...form, destination: v })}>
                  <SelectTrigger data-testid="reuse-destination"><SelectValue /></SelectTrigger>
                  <SelectContent>{DESTINATIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Nom destinatari (opcional)</Label>
                <Input value={form.destination_name} onChange={(e) => setForm({ ...form, destination_name: e.target.value })} placeholder="Ex: Banc d'aliments..." />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-semibold">Data límit</Label>
                  <Input type="date" value={form.limit_date} onChange={(e) => setForm({ ...form, limit_date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Valor estimat (€)</Label>
                  <Input type="number" min="0" step="0.01" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <Button type="submit" disabled={saving} className="w-full bg-[#7B2D3F] hover:bg-[#5B1D2F]" data-testid="reuse-submit">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#7B2D3F]" /></div>
      ) : items.length === 0 ? (
        <EmptyState icon={HeartHandshake} title="Cap acció registrada" description="Comença a registrar reutilitzacions per estalviar costos i reduir l'impacte." />
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((it) => (
            <Card key={it.reuse_action_id} className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-gray-900">{it.product_name}</p>
                  <p className="text-xs text-gray-500">{destLabel(it.destination)} {it.destination_name ? `· ${it.destination_name}` : ""}</p>
                </div>
                <Badge className={STATUS_COLORS[it.status] || "bg-gray-100 text-gray-700"}>
                  {it.status === "planned" ? "Planificat" : it.status === "done" ? "Completat" : "Descart"}
                </Badge>
              </div>
              <div className="mt-2 flex items-center gap-3 text-sm">
                <span className="text-gray-700">{(it.quantity || 0).toFixed(2)} {it.unit}</span>
                {it.estimated_value > 0 && <span className="text-emerald-700 font-semibold">{it.estimated_value.toFixed(2)} € recuperats</span>}
                {it.limit_date && <span className="text-gray-500 text-xs ml-auto">Límit: {it.limit_date}</span>}
              </div>
              {it.notes && <p className="text-xs text-gray-500 mt-2">{it.notes}</p>}
              <div className="mt-3 flex items-center gap-2">
                {it.status !== "done" && (
                  <Button size="sm" variant="outline" onClick={() => markDone(it)} data-testid={`reuse-done-${it.reuse_action_id}`}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Completar
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(it)} data-testid={`reuse-delete-${it.reuse_action_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
